import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Safe sessionStorage wrapper — returns null and swallows SecurityError
 * thrown by Safari in Private Browsing mode.
 */
function safeSession(op: 'get' | 'set' | 'remove', key: string, val?: string): string | null {
  try {
    if (op === 'get') return sessionStorage.getItem(key);
    if (op === 'set') { sessionStorage.setItem(key, val!); return null; }
    if (op === 'remove') { sessionStorage.removeItem(key); return null; }
  } catch { /* Safari Private Browsing throws SecurityError */ }
  return null;
}

/**
 * Detects whether the app is running in a mobile context.
 *
 * Priority order:
 *   1. ?mobile=1 query param (dev-only override — Chrome DevTools testing)
 *   2. ?mobile=0 query param (force desktop in Capacitor WebView)
 *   3. Capacitor user agent (native app)
 *   4. Screen width < 768px (reactive — updates on resize/orientation change)
 *
 * The ?mobile=1 param is only honoured in dev/test builds. In production
 * it is ignored to prevent cosmetic phishing (URL-based forced mobile layout).
 *
 * Returns false during SSR (no window).
 */
export function useIsMobile(): boolean {
  const [search] = useSearchParams();

  // All hooks must be called unconditionally before any early returns.
  const [isMobileWidth, setIsMobileWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setIsMobileWidth(window.innerWidth < 768);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Query param overrides — dev/test only. Persist to sessionStorage
  // so ?mobile=1 survives client-side navigation (React Router strips params).
  if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
    const mobileParam = search.get('mobile');
    if (mobileParam === '1') { safeSession('set', 'force-mobile', '1'); return true; }
    if (mobileParam === '0') { safeSession('remove', 'force-mobile'); return false; }
    if (safeSession('get', 'force-mobile') === '1') return true;
  }

  // SSR guard
  if (typeof window === 'undefined') return false;

  // Capacitor native WebView
  if (navigator.userAgent.includes('Capacitor')) return true;

  // Reactive screen-width check (updates on resize / device rotation)
  return isMobileWidth;
}

/**
 * Non-reactive version for use outside React components (e.g., service functions).
 * Does NOT check query params — only platform + screen width.
 */
export function isMobileScreen(): boolean {
  if (typeof window === 'undefined') return false;
  if (navigator.userAgent.includes('Capacitor')) return true;
  return window.innerWidth < 768;
}
